import { useState } from "react";
import { mediaUrl } from "../../../mediaAuth";
import { formatDuration, formatFutureRelativeTime, formatRelativeTime, videoMime } from "../../../utils";
import { useVideoPosterFrame } from "../../hooks/useVideoPosterFrame";
import type { Gift } from "../../../types";

// Privacy rule (video playback audit-style reasoning, applied to gifts):
// a real thumbnail is only ever safe to render when it can't spoil a
// reveal or leak something the viewer isn't authorized to see yet.
//
// - Sent gifts: the server's own GET /api/gifts/:id/media is recipient-only
//   (403 for the sender - see server.js) - there is literally no
//   authorized way to fetch a preview of your own sent gift, "existing
//   authorized behavior" doesn't cover this, so sent gifts always use the
//   protected treatment.
// - Received, locked: obviously protected - that's the point of a lock.
// - Received, mode "once", not yet viewed: showing the real image would
//   spoil the one-time reveal this mode exists for.
// - Received, mode "once", already viewed: the server has already deleted
//   the file (GiftsDB.markViewed) - attachment is null, nothing to show.
// - Received, mode "keep", not yet opened even once: still protected -
//   the FIRST open is still meant to feel like unwrapping something,
//   even though the file persists afterward (matches the reference
//   mockup's own "unopened received gift" example, which uses the gift
//   icon, not a real thumbnail).
// - Received, mode "keep", opened at least once: safe - the recipient has
//   already seen it, and it's designed to stay viewable indefinitely.
function canShowRealThumbnail(gift: Gift): boolean {
  return gift.direction === "received" && !gift.locked && gift.mode === "keep" && !!gift.viewedAt && !!gift.attachment;
}

// Virtual gift art is never privacy-sensitive - it's a shared catalog
// asset (an emoji today, real artwork later per server.js's own comment),
// not the recipient's own private photo/video, so unlike custom gifts it
// always renders regardless of direction/viewedAt.
function VirtualPreview({ gift }: { gift: Gift }) {
  return (
    <div className="gift-card-virtual-art" aria-hidden="true">
      {gift.catalogEmoji || "🎁"}
    </div>
  );
}

function ProtectedPreview({ gift }: { gift: Gift }) {
  // A "once" gift keeps its attachment (and thus its real type) right up
  // until the moment it's actually viewed, so the protected placeholder
  // can still show the correct photo/video/voice glyph instead of a
  // generic one.
  const isVideo = gift.attachment?.type === "video";
  const isVoice = gift.attachment?.type === "audio";
  const locked = gift.locked;
  return (
    <div className={`gift-card-protected${locked ? " is-locked" : ""}`}>
      <i
        className={`bi ${locked ? "bi-hourglass-split" : isVoice ? "bi-mic-fill" : isVideo ? "bi-play-circle-fill" : "bi-gift-fill"}`}
        aria-hidden="true"
      />
    </div>
  );
}

function RealPreview({ gift }: { gift: Gift }) {
  const attachment = gift.attachment!;
  const isVideo = attachment.type === "video";
  const isVoice = attachment.type === "audio";
  const [duration, setDuration] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const videoRef = useVideoPosterFrame(attachment.filename, isVideo, "gift-card-poster", setDuration);
  const src = mediaUrl(`/api/gifts/${gift.id}/media`);

  if (failed) {
    return (
      <div className="gift-card-protected">
        <i className={`bi ${isVoice ? "bi-mic-fill" : isVideo ? "bi-play-circle-fill" : "bi-gift-fill"}`} aria-hidden="true" />
      </div>
    );
  }

  // A row-level thumbnail isn't valuable for audio (there's nothing to
  // show, and playback belongs in the full reveal viewer) - a simple mic
  // badge instead, same treatment as the protected state.
  if (isVoice) {
    return (
      <div className="gift-card-media gift-card-media-voice">
        <i className="bi bi-mic-fill" aria-hidden="true" />
      </div>
    );
  }

  if (isVideo) {
    return (
      <div className="gift-card-media">
        <video ref={videoRef} preload="none" muted playsInline onError={() => setFailed(true)}>
          <source src={src} type={videoMime(attachment.filename)} />
        </video>
        <span className="gift-card-play" aria-hidden="true">
          <i className="bi bi-play-fill" />
        </span>
        {duration !== null && <span className="gift-card-duration">{formatDuration(duration)}</span>}
      </div>
    );
  }

  return (
    <div className="gift-card-media">
      <img src={src} alt="" onError={() => setFailed(true)} />
    </div>
  );
}

export function GiftCard({ gift, onView, onSaveToAlbum }: { gift: Gift; onView: (gift: Gift) => void; onSaveToAlbum: (gift: Gift) => void }) {
  const isReceived = gift.direction === "received";
  const isVirtual = gift.type === "virtual";
  const isSaved = gift.mode === "keep" && !!gift.albumId;
  // Mode (View once/Keep/Saved) is a custom-gift concept - a virtual gift
  // is always mode "keep" under the hood (server-enforced, no file to
  // protect), so its secondary line shows which catalog gift it is
  // instead, matching the reference's "🌹 Rose" framing.
  const modeLabel = isVirtual ? gift.catalogName || "Gift" : gift.mode === "once" ? "View once" : isSaved ? `Saved to ${gift.albumName}` : "Keep";
  const isUnopened = isReceived && !gift.locked && !gift.viewedAt;
  // See canSaveToAlbum's original definition (GiftsScreen.tsx before this
  // redesign) - this used to be permanently unreachable, since the "can
  // view" condition it was gated behind is a strict superset of this one
  // (whenever this is true, so is "can view", and "can view" always won
  // the ternary first). Surfaced here as its own small affordance instead
  // so it's actually reachable, alongside (not instead of) the card's own
  // tap-to-view.
  const canSaveToAlbum = isReceived && !gift.locked && gift.mode === "keep" && !isSaved && !!gift.attachment;

  const directionLabel = isReceived ? `From ${gift.senderName}` : `To ${gift.recipientName}`;
  const showReal = canShowRealThumbnail(gift);

  return (
    <button type="button" className={`gift-card${isUnopened ? " is-unopened" : ""}`} onClick={() => onView(gift)} aria-label={`${directionLabel}, ${modeLabel}, ${formatRelativeTime(gift.createdAt)}`}>
      {isVirtual ? <VirtualPreview gift={gift} /> : showReal ? <RealPreview gift={gift} /> : <ProtectedPreview gift={gift} />}

      <span className="gift-card-body">
        <span className="gift-card-direction">{directionLabel}</span>
        <span className="gift-card-meta">
          {modeLabel} · {formatRelativeTime(gift.createdAt)}
        </span>
        {gift.message && <span className="gift-card-message">&ldquo;{gift.message}&rdquo;</span>}
      </span>

      <span className="gift-card-status">
        {gift.locked ? (
          <span className="gift-card-locked" aria-label={`Unlocks ${formatFutureRelativeTime(gift.unlockAt!)}`}>
            <i className="bi bi-hourglass-split" aria-hidden="true" />
            {formatFutureRelativeTime(gift.unlockAt!)}
          </span>
        ) : isUnopened ? (
          <span className="gift-card-new" aria-label="New, unopened gift">
            NEW
          </span>
        ) : canSaveToAlbum ? (
          <span
            role="button"
            tabIndex={0}
            className="gift-card-save"
            aria-label="Save gift to an album"
            onClick={(e) => {
              e.stopPropagation();
              onSaveToAlbum(gift);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                onSaveToAlbum(gift);
              }
            }}
          >
            <i className="bi bi-bookmark-plus" aria-hidden="true" />
            Save
          </span>
        ) : !isReceived ? (
          <span className="gift-card-pending-viewed">{gift.viewedAt ? "Viewed" : "Pending"}</span>
        ) : null}
        <i className="bi bi-chevron-right gift-card-chevron" aria-hidden="true" />
      </span>
    </button>
  );
}
